import { defineCollection } from "@rebasepro/cms-types";
import playersCollection from "./players";
import type { CollectionConfig } from "@rebasepro/types";

// A player reporting another (songbuzz_friend_report), one row per reporter and person reported. backend/src/friends.ts
// creates the table and writes it; here admins read them and clear the ones dealt with. `who` reported `about`
// (both accounts, as in Players), `name` is the name they saw.
const reportsCollection = defineCollection({
    name: "Reports",
    singularName: "Report",
    slug: "reports",
    table: "songbuzz_friend_report",
    description: "Players reporting another player, and why. Clear a report once it's dealt with.",
    securityRules: [
        { operation: "select", roles: ["admin"] },
        { operation: "delete", roles: ["admin"] },
    ],
    properties: {
        who: { name: "Reported by (id)", type: "string", isId: "manual", admin: { readOnly: true, hideFromCollection: true } },
        about: { name: "Reported (id)", type: "string", isId: "manual", admin: { readOnly: true, hideFromCollection: true } },
        reportedPlayer: {
            name: "Reported player", type: "relation",
            relation: { kind: "via", cardinality: "one", target: (): CollectionConfig => playersCollection, relationName: "reportedPlayer", joinPath: [{ table: "songbuzz_players", on: { from: "about", to: "id" } }] },
            admin: { readOnly: true },
        },
        reporter: {
            name: "Reported by", type: "relation",
            relation: { kind: "via", cardinality: "one", target: (): CollectionConfig => playersCollection, relationName: "reporter", joinPath: [{ table: "songbuzz_players", on: { from: "who", to: "id" } }] },
            admin: { readOnly: true },
        },
        name: { name: "Name they saw", type: "string", admin: { readOnly: true } },
        why: { name: "Why", type: "string", admin: { readOnly: true, multiline: true } },
        at: { name: "When", type: "date", autoValue: "on_create", validation: { required: true }, admin: { readOnly: true } },
    },
    admin: {
        icon: "Flag",
        group: "Players",
        openEntityMode: "side_panel",
        sort: ["at", "desc"],
        display: {
            title: ({ entity }) => entity.values.name || "No name",
            subtitle: "why",
            date: "at",
            // how many people have reported the same person: one report is noise, five are a pattern
            status: async ({ entity, context }) => {
                const about = entity.values.about;
                if (!about) return undefined;
                const n = await context.client.data.collection("reports").count({ where: { about: ["==", about] } });
                return n > 1 ? `${n} reports` : undefined;
            },
        },
        form: {
            sections: [
                { key: "report", properties: ["reportedPlayer", "name", "why", "reporter", "at"] },
                { key: "ids", title: "Behind the scenes", collapsed: true, properties: ["who", "about"] },
            ],
        },
    },
});

export default reportsCollection;
